import React, { useEffect, useState, useMemo, useRef } from "react";
import SelectOption from "./SelectOption";
import { ReactComponent as AddIcon } from "../../../Assets/Icons/addNewIcon.svg";
import { ReactComponent as InfoIcon } from "../../../Assets/Icons/info.svg";
import { ReactComponent as WhitePlainTick } from "../../../Assets/Icons/whiteplaintick.svg";
import { ReactComponent as BlueCrossIcon } from "../../../Assets/Icons/blueCross.svg";
import { ReactComponent as EditIcon } from "../../../Assets/Icons/editIcon.svg";
import { ReactComponent as DeleteIcon } from "../../../Assets/Icons/deleteIcon.svg";
import { Tooltip } from "antd";
import Input from "../FormInputs";
import { useDispatch, useSelector } from "react-redux";
import { createOffer, createSubCategory, getSubCategories, deleteSubCategory } from "../../../services/offerService";
import { useFormik } from "formik";
import PrimaryButton from "../PrimaryButton";
import { subCategorySchema } from "../../../utils/validationSchemas";
import toast from "../../../utils/toast";
import { useOfferFormContext } from "./OfferFormContext";
import Checkbox from "antd/lib/checkbox/Checkbox";
import { scrollToFirstError } from "../../../utils/utilFunctions";

const SubCategorySelection = ({
  categoryId,
  subCategories,
  setSubCategories,
  stepIndex,
  setStepIndex,
  handlePrevious,
  setPayloadData,
  editOfferData,
  isEdit,
  selectedMerchant,
  categories,
  handleSaveAndExit,
  payload,
}) => {
  const dispatch = useDispatch();
  const loading = useSelector((state) => state?.offerReducer?.loading);
  const offerData = useSelector((state) => state?.offerReducer?.createdOffer);
  const role = useSelector(
    (state) => state?.authReducer?.tokenData?.user?.role
  );

  const { registerNextAction, setIsNextDisabled } = useOfferFormContext();

  // Get the selected category name for dynamic title
  const selectedCategoryName = useMemo(() => {
    // First try to get from editOfferData (edit mode)
    if (isEdit && editOfferData?.category?.title) {
      return editOfferData.category.title;
    }
    // Then try to get from offerData (new offer creation)
    if (offerData?.category?.title) {
      return offerData.category.title;
    }
    // Fallback: look up in categories array by categoryId
    if (categoryId && categories?.length > 0) {
      const category = categories.find((cat) => cat?._id === categoryId);
      return category?.title || "";
    }
    return "";
  }, [isEdit, editOfferData?.category?.title, offerData?.category?.title, categoryId, categories]);
  const [selectedSubCategories, setSelectedSubCategories] = useState([]);
  const [selectedSubCategoriesIds, setSelectedSubCategoriesIds] = useState([]);
  const [addClicked, setAddClicked] = useState(false);
  const [newSubCategory, setNewSubCategory] = useState("");
  const [inputNonce, setInputNonce] = useState(0);
  const [subNames, setSubNames] = useState([]);
  const [offerPendingSub, setOfferPendingSub] = useState([]);
  const [isAddingSubCategory, setIsAddingSubCategory] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [deletedSubCategories, setDeletedSubCategories] = useState([]);
  const [hiddenCustomIds, setHiddenCustomIds] = useState([]);

  const handleEditClick = (subCat) => {
    setEditingId(subCat._id);
    setEditValue(subCat.title);
  };

  const handleSaveEdit = (subCat) => {
    // Logic to save. For now, we update local lists to reflect change visually if ID matches.
    if (!editValue.trim()) return;

    setSubNames(prev => prev.map(n => n === subCat.title ? editValue : n));
    setFieldValue("subCategory", formik.values.subCategory.map(n => n === subCat.title ? editValue : n));

    // Update local lists with simple object update
    const updateList = (list) => list.map(s => s._id === subCat._id ? { ...s, title: editValue } : s);
    setSubCategories(prev => updateList(prev));
    setOfferPendingSub(prev => updateList(prev));
    setSelectedSubCategories(prev => updateList(prev));

    setEditingId(null);
    setEditValue("");
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditValue("");
  };

  const handleDeleteClick = (subCat) => {
    const sid = subCat?._id || subCat?.id;
    if (!sid) return;

    // Logic to hide local reference instead of purging from master subCategories list.
    // This prevents 'already exists' errors when re-adding the same name.
    setHiddenCustomIds(prev => [...prev, sid]);

    const filterFunc = (item) => (item?._id || item?.id) !== sid;

    setOfferPendingSub(prev => prev.filter(filterFunc));
    setSelectedSubCategories(prev => prev.filter(filterFunc));
    setSelectedSubCategoriesIds(prev => prev.filter(id => id !== sid));
    setSubNames(prev => prev.filter(n => n !== subCat.title));
    const currentList = formik.values.subCategory || [];
    setFieldValue("subCategory", currentList.filter(n => n !== subCat.title));

    // Store in deleted list to allow restoration if added back
    setDeletedSubCategories(prev => [...prev, subCat]);

    // Dispatch DELETE API call for server-side persistence
    dispatch(deleteSubCategory({ id: sid, merchantId: selectedMerchant?._id }));
  };

  const initialSyncDone = useRef(false);

  // Reset sync flag if switching to a different offer
  useEffect(() => {
    initialSyncDone.current = false;
  }, [offerData?._id]);

  useEffect(() => {
    // Only run the synchronization logic if we haven't done it yet.
    // We removed 'selectedSubCategoriesIds.length > 0' check because it caused 
    // valid specific states (like empty selection after delete) to be overwritten 
    // when subCategories list updated (e.g. adding new custom one).
    if (initialSyncDone.current) {
      return;
    }

    let subsToProcess = [];

    if (Array.isArray(payload?.subCategory) && payload.subCategory.length > 0) {
      subsToProcess = payload.subCategory;
    } else if (Array.isArray(offerData?.subCategory) && offerData.subCategory.length > 0) {
      subsToProcess = offerData.subCategory;
    } else if (isEdit && Array.isArray(editOfferData?.subCategory)) {
      subsToProcess = editOfferData.subCategory;
    }

    // If we have nothing to sync from, don't reset everything yet if we already have local state
    if (subsToProcess.length === 0 && selectedSubCategoriesIds.length > 0) {
      initialSyncDone.current = true;
      return;
    }

    if (subsToProcess.length > 0) {
      // Reset relevant states before repopulating
      const newSelected = [];
      const newIds = [];
      const newNames = [];
      const newPending = [];

      subsToProcess.forEach(item => {
        let subObj = null;

        // If it's already an object
        if (typeof item === 'object' && item !== null && (item._id || item.id)) {
          subObj = item;
        }
        // If it's just an ID string, find it in the available subCategories list
        else if (typeof item === 'string') {
          subObj = (subCategories || []).find(s => s._id === item) ||
            (offerPendingSub || []).find(s => s._id === item);
        }

        if (subObj) {
          const sid = subObj._id || subObj.id;
          if (!newIds.includes(sid)) {
            newSelected.push(subObj);
            newIds.push(sid);
            newNames.push(subObj.title);

            // Track pending/rejected ones locally
            if (subObj.status === "pending" || subObj.status === "rejected" || subObj.status === true) {
              newPending.push(subObj);
            }
          }
        }
      });

      setSelectedSubCategories(newSelected);
      setSelectedSubCategoriesIds(newIds);
      setSubNames(newNames);
      setFieldValue("subCategory", newNames);
      setOfferPendingSub(prev => {
        const unique = [...prev];
        newPending.forEach(p => {
          if (!unique.some(u => u._id === p._id)) unique.push(p);
        });
        return unique;
      });
    }

    // Only mark sync as done if we actually had subcategories valid to map against, 
    // or if we simply had nothing to process (valid empty state).
    // This prevents "done" state if subCategories were not loaded yet during a non-empty sync attempt.
    if ((subCategories && subCategories.length > 0) || subsToProcess.length === 0) {
      initialSyncDone.current = true;
    }
  }, [offerData?._id, editOfferData, isEdit, subCategories]); // Initial load and offer switching triggers this

  // Normalize and split predefined vs custom for clear UI
  const normalizeTitle = (t) => String(t || "").trim().toLowerCase();
  const isCustomSub = (s) =>
    s?.isCustom === true || s?.merchantId || s?.merchantid || s?.merchant?._id;
  const uniqueByTitle = (arr) => {
    const seen = new Set();
    const out = [];
    for (const item of arr || []) {
      const key = normalizeTitle(item?.title);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      out.push(item);
    }
    return out;
  };

  const allApproved = uniqueByTitle(
    (subCategories || []).filter((sub) => sub?.status === "approved")
  );
  const allCustomApproved = uniqueByTitle(allApproved.filter(isCustomSub));
  const customTitles = new Set(allCustomApproved.map((s) => normalizeTitle(s.title)));
  const generalApproved = uniqueByTitle(
    allApproved.filter((s) => !customTitles.has(normalizeTitle(s.title)))
  );

  const initialValues = {
    subCategory: [],
  };

  const currentPayload = {
    category: categoryId,
    merchantId: selectedMerchant?._id,
    subCategory: selectedSubCategoriesIds,
    id: offerData?._id,
    isCreated: false,
  };

  useEffect(() => {
    setPayloadData(currentPayload);
  }, [categoryId, selectedSubCategoriesIds, offerData?._id]);

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: subCategorySchema,

    onSubmit: async (values) => {
      // If user started adding a custom sub-category, auto-save it before Next
      if (addClicked && (newSubCategory || "").trim().length > 0) {
        await handleNewSubCategorySubmit();
      }

      const hasRejectedSubCategory = selectedSubCategories?.some(
        (subCat) => subCat.status === "rejected"
      );
      if (hasRejectedSubCategory) {
        toast?.error("You cannot proceed with rejected sub category selected.");
        return;
      }
      const createPromise = await dispatch(
        createOffer({ ...currentPayload, offerId: offerData?._id })
      )?.unwrap();
      if (createPromise?.success === true) {
        setStepIndex(stepIndex + 1);
      }
    },
  });

  const {
    handleChange,
    handleBlur,
    errors,
    touched,
    setFieldValue,
    handleSubmit,
  } = formik;

  const attemptNext = async () => {
    formik.setTouched({ subCategory: true });
    const validationErrors = await formik.validateForm();
    if (Object.keys(validationErrors || {}).length > 0) {
      scrollToFirstError(validationErrors, ["subCategory"]);
      return;
    }
    formik.handleSubmit();
  };

  // Register the Next action
  useEffect(() => {
    registerNextAction(() => attemptNext());
  }, [registerNextAction, formik.values, addClicked, newSubCategory]);

  // Update disabled state
  useEffect(() => {
    const isDisabled = selectedSubCategoriesIds.length === 0;
    setIsNextDisabled(isDisabled);
  }, [selectedSubCategoriesIds, setIsNextDisabled]);

  // Helper to get exactly what is rendered
  const getRenderedItems = () => {
    const approved = (subCategories || []).filter((s) => s?.status === "approved");
    const pending = offerPendingSub || [];
    return [...approved, ...pending];
  };

  const isAllSelected = (() => {
    const allRendered = getRenderedItems();
    return allRendered?.length > 0 &&
      allRendered.every((subCat) => selectedSubCategoriesIds.includes(subCat?._id));
  })();

  const handleSelectAll = () => {
    const allRendered = getRenderedItems();

    // Check if everything visible is currently selected
    const allSelected = allRendered?.length > 0 &&
      allRendered.every((subCat) => selectedSubCategoriesIds.includes(subCat?._id));

    if (allSelected) {
      // DESELECT ALL visible items
      const updatedSubCategories = selectedSubCategories.filter(
        (subCat) => !allRendered.some((rendered) => rendered?._id === subCat?._id)
      );
      setSelectedSubCategories(updatedSubCategories);

      const updatedIds = selectedSubCategoriesIds.filter(
        (id) => !allRendered.some((rendered) => rendered?._id === id)
      );
      setSelectedSubCategoriesIds(updatedIds);

      // For titles/names, we must be careful not to remove a name if it's used by a selected item outside of this list (unlikely here but good practice)
      // Simpler approach: rebuild names from the new selected list
      const newNames = updatedSubCategories.map(s => s?.title);
      setSubNames(newNames);
      setFieldValue("subCategory", newNames);

    } else {
      // SELECT ALL visible items
      const newSubCategories = [...selectedSubCategories];
      const newIds = [...selectedSubCategoriesIds];
      // We don't rely on existing names state, we'll rebuild it to be safe or just append

      allRendered.forEach((subCat) => {
        if (!newIds.includes(subCat?._id)) {
          newSubCategories.push(subCat);
          newIds.push(subCat?._id);
        }
      });

      const newNames = newSubCategories.map(s => s?.title);

      setSelectedSubCategories(newSubCategories);
      setSelectedSubCategoriesIds(newIds);
      setSubNames(newNames);
      setFieldValue("subCategory", newNames);
    }
  };

  const handleClick = (id, title) => {
    const isSelected = selectedSubCategories.find(
      (subCat) => subCat?._id === id
    );

    if (isSelected) {
      const updatedSubCategories = selectedSubCategories.filter(
        (subCat) => subCat?._id !== id
      );
      setSelectedSubCategories(updatedSubCategories);

      const updatedIds = selectedSubCategoriesIds.filter(
        (subCategoryId) => subCategoryId !== id
      );
      setSelectedSubCategoriesIds(updatedIds);

      const updatedNames = subNames.filter((t) => t !== title);
      setSubNames(updatedNames);
      setFieldValue("subCategory", updatedNames);
    } else {
      const subCategory = subCategories?.find((subCat) => subCat?._id === id);
      const updatedNames = [...subNames, subCategory?.title];
      setSelectedSubCategories((prev) => [...prev, subCategory]);
      setSelectedSubCategoriesIds((prev) => [...prev, subCategory?._id]);
      setSubNames(updatedNames);
      setFieldValue("subCategory", updatedNames);
    }
  };

  const isSubCategoryClicked = (subCat) => {
    return selectedSubCategories.find(
      (selectedCat) => selectedCat?._id === subCat?._id
    );
  };

  const handleAddClick = () => {
    setNewSubCategory("");
    setInputNonce((n) => n + 1);
    setAddClicked(true);
  };

  const handleNewSubCategoryChange = (e) => {
    setNewSubCategory(e.target.value);
  };

  const handleNewSubCategorySubmit = async () => {
    const titleToCreate = (newSubCategory || "").trim();
    if (titleToCreate !== "") {
      setIsAddingSubCategory(true);
      try {
        let parentOfferId = offerData?._id || editOfferData?._id;

        // If no offer exists yet, we must create it first to attach the sub-category
        if (!parentOfferId) {
          const initialOfferPayload = {
            category: categoryId,
            merchantId: selectedMerchant?._id, // For Admin flow
            subCategory: [], // No subcategories yet
            isCreated: false,
          };

          const offerResponse = await dispatch(createOffer(initialOfferPayload))?.unwrap();
          if (offerResponse?.success) {
            parentOfferId = offerResponse?.data?._id;
            // Optionally update Redux or local state if not auto-handled,
            // but relying on the unwrapped response is safest for immediate use.
          } else {
            console.error("Failed to create initial offer for subcategory");
            toast.error("Failed to initialize offer. Please try again.");
            return;
          }
        }

        if (!parentOfferId) {
          toast.error("Unable to verify Offer ID.");
          return;
        }

        const payload = role === "merchant" ? {
          title: titleToCreate,
          status: true,
          category: categoryId,
          offer: parentOfferId,
          isCustom: true,
        } : {
          title: titleToCreate,
          status: true,
          category: categoryId,
          offer: parentOfferId,
          isCustom: true,
          merchantId: offerData?.merchant?._id || selectedMerchant?._id,
        };
        const normToCreate = titleToCreate.toLowerCase();

        // 1. Check if it's in the 'deleted' list
        // If it was deleted, it's gone from the server. We must CREATE it again (get a new ID).
        // We cannot simply restore the old local object because its ID is invalid on the backend.
        const deletedMatch = deletedSubCategories.find(s => s.title.toLowerCase() === normToCreate);
        if (deletedMatch) {
          // Verify if it's actually in unique lists before "removing" (cleanup)
          setDeletedSubCategories(prev => prev.filter(s => s._id !== deletedMatch._id));
          // We do NOT return here. We proceed to create a fresh subcategory on the server.
        }

        // 2. Check if it's already in the main lists but just not selected
        // We MUST verify that the matched item is NOT hidden (deleted).
        // If it is hidden, we treat it as non-existent so we can create a fresh one (new ID).
        const isNotHidden = (s) => !hiddenCustomIds.includes(s?._id || s?.id);

        const existingMatch = (subCategories || []).filter(isNotHidden).find(s => s.title.toLowerCase() === normToCreate) ||
          (offerPendingSub || []).filter(isNotHidden).find(s => s.title.toLowerCase() === normToCreate);

        if (existingMatch) {
          // If we found a valid NON-HIDDEN match, just select it
          if (!selectedSubCategoriesIds.includes(existingMatch._id)) {
            setSelectedSubCategories(prev => [...prev, existingMatch]);
            setSelectedSubCategoriesIds(prev => [...prev, existingMatch._id]);
            setSubNames(prev => [...prev, existingMatch.title]);
            setFieldValue("subCategory", [...(formik.values.subCategory || []), existingMatch.title]);
          }
          setAddClicked(false);
          setNewSubCategory("");
          return;
        }

        const createPromise = await dispatch(
          createSubCategory(payload)
        )?.unwrap();
        if (createPromise?.success === true) {
          const created = createPromise?.data || {};
          if (created?.status === "pending" || created?.status === "rejected") {
            setOfferPendingSub((prev) => {
              const exists = (prev || []).some((p) => p?._id === created?._id);
              return exists ? prev : [...(prev || []), created];
            });
          }
          const norm = (t) => String(t || "").trim().toLowerCase();
          setSubCategories((prev) => {
            const list = Array.isArray(prev) ? prev : [];
            const normTitle = norm(created?.title);

            // Remove any "ghost" items (items with same name that were deleted/hidden)
            // This ensures we replace the old invalid ID with the new valid ID
            const cleanList = list.filter(s => {
              const isGhost = hiddenCustomIds.includes(s?._id || s?.id) && norm(s?.title) === normTitle;
              return !isGhost;
            });

            const exists = cleanList.some((s) => s?._id === created?._id || norm(s?.title) === normTitle);
            return exists ? cleanList : [...cleanList, created];
          });
          setAddClicked(false);
          setSelectedSubCategories((prev) => {
            const list = Array.isArray(prev) ? prev : [];
            const exists = list.some((s) => s?._id === created?._id);
            return exists ? list : [...list, created];
          });
          setSelectedSubCategoriesIds((prev) => {
            const list = Array.isArray(prev) ? prev : [];
            const exists = list.includes(created?._id);
            return exists ? list : [...list, created?._id];
          });
          setSubNames((prev) => {
            const list = Array.isArray(prev) ? prev : [];
            const exists = list.includes(created?.title);
            return exists ? list : [...list, created?.title];
          });
          {
            const currentList = Array.isArray(formik.values?.subCategory)
              ? formik.values.subCategory
              : [];
            const exists = currentList.includes(created?.title);
            const nextList = exists ? currentList : [...currentList, created?.title];
            setFieldValue("subCategory", nextList);
          }
          setNewSubCategory("");
          setInputNonce((n) => n + 1);
        }
      } catch (error) {
        console.error("Error creating subcategory:", error);
      } finally {
        setIsAddingSubCategory(false);
      }
    }
  };



  const addButtonLabel = isAddingSubCategory ? "Adding..." : "Add";

  return (
    <div className="w-full flex flex-col items-center justify-start flex-grow">
      <div className="w-full max-w-[800px] mt-[30px] md:mt-[60px] relative">

        {/* Title */}
        <div className="mb-6 md:mb-8 w-full text-left">
          <h2 className="text-[22px] md:text-[32px] font-[600] text-[#303030] font-poppins text-left">
            Select the Subcategory for Your <span className="text-[#00ADEF]">{selectedCategoryName}</span> Offer
          </h2>
        </div>

        {/* Top Controls: Checkbox and Count */}
        <div className="flex items-center justify-between w-full mb-6">
          <div
            className="flex items-center gap-3 cursor-pointer ml-3"
            onClick={handleSelectAll}
          >
            <div className={`w-[20px] h-[20px] rounded-[4px] border flex items-center justify-center transition-all ${isAllSelected ? "bg-[#00ADEF] border-[#00ADEF]" : "bg-white border-[#80D6F7]"
              }`}>
              {isAllSelected && <WhitePlainTick className="w-[12px] h-[12px] checkmark-pop" />}
            </div>
            <span className="text-[14px] font-[400] text-[#344054] font-poppins">
              Select All
            </span>
          </div>

          {selectedSubCategoriesIds.length > 0 && (
            <div className="text-[14px] text-[#767676] font-poppins">
              {selectedSubCategoriesIds.length} Selected
            </div>
          )}
        </div>

        {/* Grid of Subcategories */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 w-full" data-field="subCategory">
          {subCategories
            ?.filter(s => s?.status === "approved" && !isCustomSub(s))
            ?.map((subCat) => (
              <SelectOption
                key={subCat?._id || subCat?.title}
                icon={null}
                title={subCat?.title}
                clicked={isSubCategoryClicked(subCat)}
                handleClick={() => handleClick(subCat?._id, subCat?.title)}
                postIcon={null}
                showCheckmark={true}
                noMargin={true}
              />
            ))}
        </div>

        {/* Empty State */}
        {(!subCategories?.some(s => s?.status === "approved") && offerPendingSub?.length === 0) && (
          <p className="font-poppins font-[500] text-[14px] text-[#303030] text-center my-8">
            The Selected Category does not have any sub category to select!!!
          </p>
        )}

        {/* Separator */}
        <div className="flex items-center w-full my-3">
          <div className="h-[1px] bg-[#E0E0E0] flex-grow"></div>
          <span className="px-4 text-[#767676] text-[14px] font-poppins">Or</span>
          <div className="h-[1px] bg-[#E0E0E0] flex-grow"></div>
        </div>

        {/* Custom Subcategories List */}
        {(() => {
          // Combine and Dedupe
          const allItems = [...(subCategories?.filter(s => s?.status === "approved" && isCustomSub(s)) || []), ...(offerPendingSub || [])];
          const uniqueCustoms = [];
          const seenIds = new Set();
          const seenTitles = new Set();

          allItems.forEach(item => {
            const sid = item?._id || item?.id;
            const normTitle = String(item.title || "").trim().toLowerCase();
            const isHidden = hiddenCustomIds.includes(sid);

            // Item is hidden if it was manually deleted in this session and NOT currently selected
            if (isHidden && !selectedSubCategoriesIds.includes(sid)) return;

            if (sid && !seenIds.has(sid)) {
              uniqueCustoms.push(item);
              seenIds.add(sid);
              seenTitles.add(normTitle);
            } else if (!sid && !seenTitles.has(normTitle)) {
              uniqueCustoms.push(item);
              seenTitles.add(normTitle);
            }
          });

          if (uniqueCustoms.length === 0 && !addClicked) return null;

          return (
            <div className="w-full mb-4">
              <label className="block text-[#303030] text-[14px] font-[600] font-poppins mb-2">Custom Subcategory</label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 w-full">
                {uniqueCustoms.map(subCat => {
                  const isEditing = editingId === subCat._id;
                  const clicked = isSubCategoryClicked(subCat);

                  if (isEditing) {
                    return (
                      <div key={subCat._id} className="relative w-full h-[45px] flex items-center justify-between border border-[#00ADEF] rounded-[5px] !bg-white pr-3">
                        <div className="flex-grow h-full">
                          <input
                            type="text"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            placeholder="Enter custom subcategory"
                            className="w-full h-full bg-white border-none focus:ring-0 px-3 outline-none text-[13px] font-poppins text-[#303030]"
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleSaveEdit(subCat);
                              if (e.key === "Escape") handleCancelEdit();
                            }}
                          />
                        </div>
                        <div className="flex items-center gap-3">
                          <span
                            onClick={handleCancelEdit}
                            className="text-[#767676] text-[12px] font-[400] font-poppins cursor-pointer hover:underline"
                          >
                            Cancel
                          </span>
                          <span
                            onClick={() => handleSaveEdit(subCat)}
                            className="text-[#00ADEF] text-[12px] font-[500] font-poppins cursor-pointer select-none"
                          >
                            Save
                          </span>
                        </div>
                      </div>
                    );
                  } else {
                    return (
                      <div
                        key={subCat._id}
                        className={`relative border rounded-[5px] h-[45px] w-full flex items-center justify-between cursor-pointer transition-all ${clicked ? "bg-[#EBF9FE] border-[#00ADEF]" : "bg-white border-[#D9D9D9]"}`}
                        onClick={() => handleClick(subCat?._id, subCat?.title)}
                      >
                        <div className="flex items-center gap-3 ml-3 overflow-hidden">
                          <div className="flex items-center justify-center flex-shrink-0">
                            {clicked ? (
                              <div className="w-[18px] h-[18px] sm:w-[16px] sm:h-[16px] md:w-[18px] md:h-[18px] border border-[#00ADEF] rounded-[3px] bg-[#00ADEF] flex items-center justify-center">
                                <WhitePlainTick className="w-[12px] h-[12px] sm:w-[10px] sm:h-[10px] md:w-[12px] md:h-[12px] checkmark-pop" />
                              </div>
                            ) : (
                              <div className="w-[18px] h-[18px] sm:w-[16px] sm:h-[16px] md:w-[18px] md:h-[18px] border border-[#80D6F7] rounded-[3px] bg-white" />
                            )}
                          </div>
                          <span className={`text-[13px] font-poppins truncate ${clicked ? "text-[#00ADEF] font-[500]" : "text-[#767676] font-[400]"}`}>{subCat.title}</span>
                        </div>
                        <div className="flex items-center gap-2 mr-3 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                          <span onClick={() => handleEditClick(subCat)} className="text-[#00ADEF] text-[12px] font-[500] font-poppins cursor-pointer hover:underline">Edit</span>
                          <span onClick={() => handleDeleteClick(subCat)} className="flex items-center justify-center cursor-pointer">
                            <BlueCrossIcon className="w-[20px] h-[20px]" />
                          </span>
                        </div>
                      </div>
                    )
                  }
                })}

                {/* Integrated Add Field - Single Column Width */}
                {addClicked && (
                  <div className="col-span-1 w-full flex flex-col gap-1">
                    {/* Input Field */}
                    <div className="w-full">
                      <input
                        type="text"
                        placeholder="Enter custom subcategory"
                        className="w-full h-[45px] bg-white border border-[#D9D9D9] rounded-[5px] px-3 outline-none text-[13px] font-poppins text-[#303030] focus:border-[#00ADEF] transition-colors"
                        name="newSubCategory"
                        value={newSubCategory}
                        autoFocus
                        onChange={handleNewSubCategoryChange}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleNewSubCategorySubmit();
                          }
                        }}
                      />
                    </div>

                    {/* Integrated Duplicate Message */}
                    {(() => {
                      const normNew = (newSubCategory || "").trim().toLowerCase();
                      if (!normNew) return null;

                      // Filter out hidden (deleted) items so we don't flag them as duplicates
                      // This allows the user to "re-add" them (which triggers the restore logic)
                      const isNotHidden = (s) => !hiddenCustomIds.includes(s._id || s.id);

                      const existsInSubs = (subCategories || []).filter(isNotHidden).some(s => s.title.toLowerCase() === normNew);
                      const existsInPending = (offerPendingSub || []).filter(isNotHidden).some(s => s.title.toLowerCase() === normNew);

                      const exists = existsInSubs || existsInPending;

                      if (exists) {
                        return (
                          <div className="mt-[-10px] text-start">
                            <span className="text-red-600 text-[10px] md:text-[12px] font-[600]">
                              This subcategory already exists.
                            </span>
                          </div>
                        );
                      }
                      return null;
                    })()}

                    {/* Action Buttons Row */}
                    <div className="flex items-center justify-start gap-4 mt-3">
                      <span
                        onClick={() => setAddClicked(false)}
                        className="text-[#00ADEF] text-[14px] font-[400] font-poppins cursor-pointer underline hover:text-[#009BD6]"
                      >
                        Cancel
                      </span>
                      <button
                        type="button"
                        onClick={handleNewSubCategorySubmit}
                        disabled={isAddingSubCategory}
                        className={`bg-[#00ADEF] text-white px-6 py-2 rounded-[5px] text-[14px] font-[500] font-poppins transition-all hover:bg-[#009BD6] ${isAddingSubCategory ? "opacity-70 cursor-wait" : ""}`}
                      >
                        {isAddingSubCategory ? "Adding..." : "Add"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )
        })()}

        <div className="flex flex-col gap-4">

          <div className="flex items-center justify-start gap-3 mt-2">
            <button
              onClick={!addClicked ? handleAddClick : undefined}
              disabled={addClicked}
              className={`h-[45px] rounded-[5px] flex items-center justify-center gap-2 px-5 transition-colors ${addClicked
                ? "bg-[#F0F0F0] border border-[#E0E0E0] cursor-not-allowed"
                : "bg-[#00ADEF] hover:bg-[#009BD6]"
                }`}
            >
              <div className={`w-[20px] h-[20px] rounded-full flex items-center justify-center p-0 ${addClicked ? "bg-[#8C8C8C]" : "bg-white"
                }`}>
                <svg width="10" height="10" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" className="m-0 p-0">
                  <path d="M6 1V11M1 6H11" stroke={addClicked ? "#E0E0E0" : "#00ADEF"} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <span className={`font-[500] font-poppins text-[14px] ${addClicked ? "text-[#767676]" : "text-white"}`}>
                Add another type of subcategory
              </span>
            </button>
            <Tooltip
              title="Add a custom sub-category that is not available in the list above."
              color="#00ADEF"
              placement={window.innerWidth < 640 ? "top" : "right"}
              overlayInnerStyle={window.innerWidth < 640 ? { maxWidth: '250px', fontSize: '12px', padding: '8px 12px', textAlign: 'center' } : { fontSize: '12px' }}
            >
              <div className="cursor-pointer flex items-center justify-center">
                <svg width="20" height="20" viewBox="0 0 18 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M9.00033 5.56675H9.00619M9.00033 11.4334V7.76675M16.3337 8.50008C16.3337 12.5503 13.0505 15.8334 9.00033 15.8334C4.95013 15.8334 1.66699 12.5503 1.66699 8.50008C1.66699 4.44988 4.95013 1.16675 9.00033 1.16675C13.0505 1.16675 16.3337 4.44988 16.3337 8.50008Z"
                    stroke={addClicked ? "#BFBFBF" : "#00ADEF"}
                    strokeWidth="1.5"
                    strokeMiterlimit="10"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </Tooltip>
          </div>
        </div>

        {errors?.subCategory && touched?.subCategory ? (
          <span className="text-red-600 sm:text-[10px] md:text-[12px] font-[600] ms-1 mt-4 block">
            {errors?.subCategory}
          </span>
        ) : null}

      </div>
    </div>
  );
};

export default SubCategorySelection;
